import {
  CompactTable,
  defaultResponseHandler,
  Flex,
  objectArraySort,
  openNotificationWithIcon,
  PageContent,
  useUserinfo,
} from '@mss/mss-component-library';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Trash, Info } from 'react-feather';
import Fetch from '../../../common/util/_fetch';
import { notification, Tooltip } from 'antd';
import Button from '../../../common/Button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type Task = {
  id: string;
  name: string;
  hostname: string;
  queue: string;
  acknowledged: boolean;
  time_start: number;
  time_elapsed: number;
  kwargs: object;
  tags: string[];
  revoked: boolean;
};

export type Worker = {
  hostname: string;
  total: object;
  shutdown: boolean;
};

export type LockKey = {
  key: string;
  collector_id: number;
  asset_name: string;
  asset_type: string;
  collector_type: string;
  collector_key: string;
  expire_time: number;
};

const createSorter = (key: string) => (a: any, b: any) =>
  String(a[key] ?? '').localeCompare(String(b[key] ?? ''));

type ActionProps = {
  queryKey: string[];
  mutationKey: string;
  mutationUrl: string;
  mutationValue: string;
  mutationLabels: { success: string; error: string };
  buttonLabels: { active: string; stopping: string };
};
const Action = ({
  mutationValue,
  mutationKey,
  mutationUrl,
  buttonLabels,
  queryKey,
  mutationLabels,
}: ActionProps) => {
  const { t } = useTranslation();
  const [mutatingRows, setMutatingRows] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const [hasPermission] = useUserinfo();

  const mutation = useMutation({
    mutationFn: async (value: string) =>
      Fetch.delete(mutationUrl, {
        body: JSON.stringify({ [mutationKey]: value }),
      }),
    onMutate: (key: string) => {
      setMutatingRows(prev => [...prev, key]);
      queryClient.cancelQueries(queryKey);
    },
    onSuccess: (_: any, value: string) => {
      queryClient.setQueryData(queryKey, (old: any[] | undefined) =>
        old?.filter(item => item[mutationKey] !== value)
      );
      queryClient.invalidateQueries(queryKey);

      openNotificationWithIcon(
        notification.success,
        t(mutationLabels.success),
        ''
      );
    },
    onError: () =>
      openNotificationWithIcon(notification.error, t(mutationLabels.error), ''),
    onSettled: (_: any, __: any, value: string) => {
      setMutatingRows(prev => prev.filter(item => item !== value));
    },
  });
  if (!hasPermission('change_worker')) return null;
  return (
    <Button
      size={'small'}
      ghost={false}
      className="px-2 py-0 border-none m-0"
      onClick={() => {
        mutation.mutateAsync(mutationValue);
      }}
      loading={mutatingRows.includes(mutationValue)}
      disabled={mutatingRows.includes(mutationValue)}
    >
      <Flex v="center">
        <Trash size={14} className="mr-2" />
        {t(
          mutatingRows.includes(mutationValue)
            ? buttonLabels.stopping
            : buttonLabels.active
        )}
      </Flex>
    </Button>
  );
};

function LockKeyList() {
  const [t] = useTranslation(['configuration', 'common']);

  const queryKey = ['lockKeys'];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await defaultResponseHandler<LockKey[]>({
        t,
        request: Fetch.get('/api/worker/lock-keys/'),
      });
      return data;
    },
    refetchInterval: 5000,
  });

  return (
    <CompactTable
      className="mb-5"
      rowKey="name"
      loading={isLoading}
      dataSource={data}
      columns={[
        {
          title: t('Key'),
          dataIndex: 'key',
          sorter: createSorter('key'),
        },
        {
          title: t('Asset'),
          dataIndex: 'asset_name',
          sorter: createSorter('asset_name'),
        },
        {
          title: t('Type'),
          dataIndex: 'asset_type',
          sorter: createSorter('asset_type'),
        },
        {
          title: t('Collector Type'),
          dataIndex: 'collector_type',
          sorter: createSorter('collector_type'),
        },
        {
          title: t('Collector'),
          dataIndex: 'collector_key',
          sorter: createSorter('collector_key'),
        },
        {
          title: t('Expire'),
          dataIndex: 'expire_time',
          sorter: createSorter('expire_time'),
        },
        {
          title: 'Actions',
          key: 'actions',
          dataIndex: 'key',
          width: 125,
          onCell: () => ({
            className: 'p-0',
          }),
          render: (key: string) => {
            return (
              <Action
                mutationValue={key}
                mutationKey="key"
                mutationUrl="/api/worker/lock-keys/delete/"
                queryKey={queryKey}
                mutationLabels={{
                  success: 'Lock deleted successfully.',
                  error: 'Failed to delete lock.',
                }}
                buttonLabels={{ active: 'Remove', stopping: 'Removing' }}
              />
            );
          },
        },
      ]}
    />
  );
}

function WorkerList() {
  const [t] = useTranslation(['configuration', 'common']);
  const queryKey = ['workers'];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await defaultResponseHandler<Worker[]>({
        t,
        request: Fetch.get('/api/worker/workers/'),
      });
      return data;
    },
    refetchInterval: 5000,
  });

  return (
    <CompactTable
      className="mb-5"
      pagination={false}
      rowKey="hostname"
      dataSource={data}
      isLoading={isLoading}
      columns={[
        {
          title: t('Hostname'),
          dataIndex: 'hostname',
          key: 'hostname',
          defaultSortOrder: 'descend',
          sorter: createSorter('hostname'),
        },
        {
          title: t('Total'),
          dataIndex: 'total',
          key: 'total',
          width: 50,
          render: (record: object) => {
            const values = Object.values(record);
            return values.reduce(
              (accumulator, currentValue) => accumulator + currentValue,
              0
            );
          },
        },
        {
          title: 'Actions',
          key: 'actions',
          dataIndex: 'hostname',
          width: 125,
          onCell: () => ({
            className: 'p-0 align-right',
          }),

          render: (hostname: string) => {
            return (
              <Action
                mutationValue={hostname}
                mutationKey="hostname"
                mutationUrl="/api/worker/workers/shutdown/"
                queryKey={queryKey}
                mutationLabels={{
                  success: 'Worker restarted successfully.',
                  error: 'Worker restart failed',
                }}
                buttonLabels={{ active: 'Restart', stopping: 'Stopping' }}
              />
            );
          },
        },
      ]}
    />
  );
}

function TaskList() {
  const [t] = useTranslation(['configuration', 'common', 'authorization']);
  const queryKey = ['tasks'];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await defaultResponseHandler<Task[]>({
        t,
        request: Fetch.get('/api/worker/tasks/'),
      });
      return data;
    },
    refetchInterval: 5000,
  });

  return (
    <CompactTable
      rowKey="id"
      dataSource={data}
      loading={isLoading}
      className={'mb-5'}
      columns={[
        {
          title: t('ID'),
          dataIndex: 'id',
          key: 'id',
          width: 40,
          render: (record: string) => {
            return <Tooltip title={record}>{record?.substring(0, 4)}</Tooltip>;
          },
        },
        {
          title: t('Name'),
          dataIndex: 'name',
          key: 'name',
          width: 100,
          sorter: (a: any, b: any) => objectArraySort(a, b, 'name'),
          render: (record: string) => {
            return <Tooltip title={record}>{record?.split('.').pop()}</Tooltip>;
          },
        },
        {
          title: t('Queue'),
          dataIndex: 'queue',
          key: 'queue',
          width: 100,
          sorter: createSorter('queue'),
        },
        {
          title: t('Ack'),
          dataIndex: 'acknowledged',
          key: 'acknowledged',
          width: 20,
          sorter: createSorter('acknowledged'),
          render: (record: boolean) => (record ? 'Yes' : 'No'),
        },
        {
          title: t('Start'),
          dataIndex: 'time_start',
          key: 'time_start',
          width: 75,
          sorter: createSorter('time_start'),
          render: (record: number | null) => {
            if (!record) {
              return 'N/A';
            }
            return moment.unix(record).format('YYYY-MM-DD HH:mm:ss');
          },
        },
        {
          title: t('Time'),
          dataIndex: 'time_elapsed',
          key: 'time_elapsed',
          width: 20,
          sorter: (a: Task, b: Task) => {
            const valA = a.time_elapsed ?? Number.MAX_SAFE_INTEGER;
            const valB = b.time_elapsed ?? Number.MAX_SAFE_INTEGER;
            return valA - valB;
          },
          render: (record: number | null) => {
            if (!record) {
              return 'N/A';
            }
            return Math.floor(record);
          },
        },
        {
          title: t('Info'),
          dataIndex: 'kwargs',
          key: 'kwargs',
          width: 10,
          render: (record: object | null) => {
            return (
              <Tooltip title={JSON.stringify(record, null, 2)}>
                <Info size={14} className="mr-2" />
              </Tooltip>
            );
          },
        },
        {
          title: 'Actions',
          key: 'actions',
          dataIndex: 'id',
          width: 125,
          render: (id: string) => {
            return (
              <Action
                mutationValue={id}
                mutationKey="task_id"
                mutationUrl="/api/worker/tasks/revoke/"
                queryKey={queryKey}
                mutationLabels={{
                  success: 'Task revoked successfully.',
                  error: 'Could not revoke task.',
                }}
                buttonLabels={{ active: 'Revoke', stopping: 'Revoking' }}
              />
            );
          },
        },
      ]}
    />
  );
}

function Workers() {
  const [t] = useTranslation(['configuration', 'common', 'authorization']);

  return (
    <PageContent title={t('Workers')}>
      <WorkerList />
      <h5>
        {t('Tasks')}{' '}
        <Tooltip title={t('Tasks that are running more then 5 minutes.')}>
          <Info size={14} className="mr-2" />
        </Tooltip>
      </h5>
      <TaskList />
      <h5>
        {t('Locked collectors')}{' '}
        <Tooltip title={t('Collectors that are locked more then 1 minute.')}>
          <Info size={14} className="mr-2" />
        </Tooltip>
      </h5>
      <LockKeyList />
    </PageContent>
  );
}

export default Workers;
